import { APP_STATUSES, type AppStatus } from "@/types/borrower";
import { DOC_TYPE_LABELS, DOC_TYPES, type DocType } from "@/types/statement";

import { choiceFields as cardRows } from "../protocol/choice";
import type {
  BlockPosition,
  BlockPositionRow,
  BlockPositionSide,
  BlockTransaction,
  ChoiceBlocks,
  ChoiceField,
  ContextRef,
  WireChoice,
  WireChoiceOption,
} from "../protocol/protocol";
import { parseContextRef } from "../store/targets";

/**
 * The one reader of what a choice shows, so the cards never touch the wire. G1's serialize_choice
 * puts `fields` at the top level, may give a pick option a `subject` (the application it names)
 * and enriches filing items with names and types; E2's protocol.ts still types the fields under
 * `display`. This reads both, and normalises the model's
 * text permissively: unknown keys and malformed rows are dropped, never shown raw. After the
 * restack protocol.ts takes G1's shape and the `display` path goes.
 */

export interface Figure {
  label: string;
  value: string;
}

/** How a choice ended, as every card shows it. Nothing expires in the user's face: an expired
 * choice reads as superseded. */
export type ChoiceOutcome = "pending" | "approved" | "declined" | "superseded";

export interface PickSubject {
  name: string;
  appStatus: AppStatus | null;
  appliedAt: string | null;
  brokerName: string;
  brokerEmail: string;
}

export interface PickRow {
  id: string;
  /** What a typed answer names: "2" picks the second row. Null for "None of these". */
  number: number | null;
  label: string;
  description: string;
  subject: PickSubject | null;
  /** What the option names ("application:12"), when it names one. */
  contextRef: ContextRef | null;
  /** Added by the server, not the model ("None of these"): the last row. */
  system: boolean;
}

/** The user's document types, by thread file; the other files keep the agent's. */
export type FilingTypes = Record<number, DocType>;

/** One file of a filing confirmation's decide `items`: a type the user changed, or a file they
 * left out. */
export interface FilingDecideItem {
  thread_file_id: number;
  doc_type?: DocType;
  include?: false;
}

/** What confirming did to a file: on the application now, already there, or not filed. */
const FILING_OUTCOMES = ["filed", "already_filed", "refused"] as const;
export type FilingOutcome = (typeof FILING_OUTCOMES)[number];

export interface FilingItem {
  threadFileId: number;
  applicationId: number | null;
  applicationLabel: string;
  name: string;
  /** The agent's suggestion; null when it gave none. */
  docType: DocType | null;
  /** Frozen when the agent suggested it. */
  alreadyFiled: boolean;
  /** False once a confirmation left it out (its outcome stays null). */
  include: boolean;
  /** Once confirmed; null before. */
  outcome: FilingOutcome | null;
}

type G1Choice = WireChoice & { fields?: unknown; subject_ref?: unknown; subject_url?: unknown };
type G1Option = WireChoiceOption & {
  description?: unknown;
  subject?: unknown;
  ref?: unknown;
  system?: unknown;
};

/** The options that turn a choice down; any other decided option went ahead (V's view agrees). */
const DECLINE_OPTIONS = ["deny", "dismiss"];

/** The thread file name the server uses when it has none. */
const UNNAMED_FILE = "Attachment";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function text(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return typeof value === "string" ? value.trim() : "";
}

/** A date formatDate can show: it reads the leading YYYY-MM-DD; anything else is no date. */
function isoDate(value: unknown): string | null {
  const date = text(value);
  return /^\d{4}-\d{2}-\d{2}/.test(date) && !Number.isNaN(Date.parse(date)) ? date : null;
}

function positiveId(value: unknown): number | null {
  const id = typeof value === "string" ? Number(value) : value;
  return typeof id === "number" && Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * A value from a known list, forgiving case, spaces and dashes ("bank-statement" -> bank_statement),
 * or one of its labels ("Driver's License" -> drivers_license).
 */
function oneOf<T extends string>(
  values: readonly T[],
  value: unknown,
  labels?: Record<T, string>
): T | null {
  const raw = text(value).toLowerCase();
  const key = raw.replace(/[\s-]+/g, "_");
  if ((values as readonly string[]).includes(key)) return key as T;
  return values.find((candidate) => labels?.[candidate].toLowerCase() === raw) ?? null;
}

export const isDeclineOption = (optionId: string | null): boolean =>
  optionId != null && DECLINE_OPTIONS.includes(optionId);

export function choiceOutcome(choice: WireChoice): ChoiceOutcome {
  if (choice.status === "pending") return "pending";
  if (choice.status !== "decided") return "superseded";
  return isDeclineOption(choice.chosen) ? "declined" : "approved";
}

/** The card's figures, full values as the server wrote them. */
export function choiceFields(choice: WireChoice): Figure[] {
  const raw = (choice as G1Choice).fields ?? choice.display?.fields;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((field) => {
    if (!isRecord(field)) return [];
    const label = text(field.label);
    const value = text(field.value);
    return label && value ? [{ label, value }] : [];
  });
}

function pickSubject(raw: unknown): PickSubject | null {
  if (!isRecord(raw)) return null;
  const name = text(raw.name);
  if (!name) return null;
  return {
    name,
    // The stored value comes first. A label normalises to its value ("Funder Review" ->
    // funder_review) for every status except "Re-Fi Contract Sent", which needs the value G1
    // sends after the cascade.
    appStatus: oneOf(APP_STATUSES, raw.app_status),
    appliedAt: isoDate(raw.applied_at),
    brokerName: text(raw.broker_name),
    brokerEmail: text(raw.broker_email),
  };
}

/** The choice's options with a usable id; a malformed option is dropped, never rendered. */
export function choiceOptions(choice: WireChoice): G1Option[] {
  const options: unknown = choice.options;
  if (!Array.isArray(options)) return [];
  return options.filter(
    (option): option is G1Option => isRecord(option) && typeof option.id === "string" && !!option.id
  );
}

/**
 * The `send_contract` an approval option's variant sets (`_variant_options`): true on the server's
 * Approve + Send Contract, false on the plain approve of a call that sends; null when it sets none.
 */
export function optionSendsContract(option: WireChoiceOption): boolean | null {
  const overrides: unknown = option.overrides;
  return isRecord(overrides) && typeof overrides.send_contract === "boolean"
    ? overrides.send_contract
    : null;
}

export function pickRows(choice: WireChoice): PickRow[] {
  const options = choiceOptions(choice);
  const isSystem = (option: G1Option) => option.system === true;
  const row = (option: G1Option, number: number | null): PickRow => ({
    id: option.id,
    number,
    label: text(option.label),
    description: text(option.description),
    subject: pickSubject(option.subject),
    contextRef: parseContextRef(option.ref)?.ref ?? null,
    system: isSystem(option),
  });
  // A typed number counts the model's options only.
  return [
    ...options.filter((option) => !isSystem(option)).map((option, index) => row(option, index + 1)),
    ...options.filter(isSystem).map((option) => row(option, null)),
  ];
}

export function filingItems(choice: WireChoice): FilingItem[] {
  const items: unknown = choice.items;
  if (!Array.isArray(items)) return [];
  return items.flatMap((raw) => {
    const threadFileId = isRecord(raw) ? positiveId(raw.thread_file_id) : null;
    if (!isRecord(raw) || threadFileId == null) return [];
    return [
      {
        threadFileId,
        applicationId: positiveId(raw.application_id),
        applicationLabel: text(raw.application_label),
        name: text(raw.name) || UNNAMED_FILE,
        docType: oneOf(DOC_TYPES, raw.doc_type, DOC_TYPE_LABELS),
        alreadyFiled: raw.already_filed === true,
        include: raw.include !== false,
        outcome: oneOf(FILING_OUTCOMES, raw.outcome),
      },
    ];
  });
}

/**
 * What the choice is about, as the server links it: its context ref and the absolute app URL (null
 * when there is none, or the user can no longer see it).
 */
export function choiceSubject(choice: WireChoice): { ref: ContextRef; url: string } | null {
  const raw = choice as G1Choice;
  const ref = parseContextRef(raw.subject_ref)?.ref;
  const url = text(raw.subject_url);
  return ref && url ? { ref, url } : null;
}

/** decide's `items` for a filing confirmation: the files left out, and the types the user changed. */
export function filingDecideItems(
  items: FilingItem[],
  types: FilingTypes,
  leftOut: ReadonlySet<number>
): FilingDecideItem[] {
  return items.flatMap((item): FilingDecideItem[] => {
    const id = item.threadFileId;
    if (leftOut.has(id)) return [{ thread_file_id: id, include: false }];
    const docType = types[id];
    return docType && docType !== item.docType ? [{ thread_file_id: id, doc_type: docType }] : [];
  });
}

/** Whether the file is on its application: what confirming did, else whether it already was. */
export const isFiled = (item: FilingItem): boolean =>
  item.outcome ? item.outcome !== "refused" : item.alreadyFiled;

/**
 * A filing the server closed at once because every file was already on its application, read
 * from its shape (cancelled, every item already filed), never from its reason's text.
 */
export function isAlreadyFiled(choice: WireChoice): boolean {
  if (choice.kind !== "filing" || choice.status !== "cancelled") return false;
  const items = filingItems(choice);
  return items.length > 0 && items.every((item) => item.alreadyFiled);
}

/**
 * An approved removal of a document from its application that ran: it can undo what an earlier
 * filing's receipt says. The wire names no document, so it cannot tell which filing it undid.
 */
export const isUnfiled = (choice: WireChoice): boolean =>
  choice.tool === "unfile_document" &&
  choiceOutcome(choice) === "approved" &&
  choice.executed === true;

/** A list card row drawn as a transaction: the row's tick and outcome, and the table's cells. */
export interface TransactionRow {
  key: string;
  ticked: boolean;
  /** The counterparty heading the row sits under. */
  group: string;
  /** "Mar 4 · $9,500.00", the row's name for a screen reader. */
  label: string;
  /** Why the row can't be ticked (a reopened card's call no longer has it), or "". */
  disabled: string;
  state: ChoiceField["state"] | null;
  line: string;
  transaction: BlockTransaction;
}

/** An approval whose rows are transactions (reclassify_transactions): the run, figures and table. */
export interface TransactionCard {
  applicationId: number;
  runId: number;
  source: "statements" | "plaid";
  stats: NonNullable<ChoiceBlocks["stats"]> | null;
  rows: TransactionRow[];
  /** The fields the table and tiles don't show (the Application). */
  figures: Figure[];
}

const blockText = (value: unknown): string => (typeof value === "string" ? value : "");

function blockTransaction(raw: unknown): BlockTransaction | null {
  if (!isRecord(raw) || !blockText(raw.key) || !blockText(raw.override_key)) return null;
  const change = (item: unknown) => ({
    old: isRecord(item) ? blockText(item.old) : "",
    new: isRecord(item) ? blockText(item.new) : "",
  });
  return {
    key: blockText(raw.key),
    override_key: blockText(raw.override_key),
    account_last4: blockText(raw.account_last4),
    date: blockText(raw.date),
    amount: blockText(raw.amount),
    ...(blockText(raw.balance) ? { balance: blockText(raw.balance) } : {}),
    count: Number.isInteger(raw.count) && (raw.count as number) > 1 ? (raw.count as number) : 1,
    description: blockText(raw.description),
    memo: blockText(raw.memo),
    counterparty: blockText(raw.counterparty),
    category: change(raw.category),
    revenue: change(raw.revenue),
  };
}

/**
 * `read` once per choice object (the store replaces it when it changes): the thread asks for a
 * card on every store update to pick it.
 */
function perChoice<T>(read: (choice: WireChoice) => T): (choice: WireChoice) => T {
  const cards = new WeakMap<WireChoice, T>();
  return (choice) => {
    if (!cards.has(choice)) cards.set(choice, read(choice));
    return cards.get(choice)!;
  };
}

/** The run a correction card is about, and its top-row figures. */
function analysisOf(blocks: Record<string, unknown>) {
  if (!isRecord(blocks.analysis)) return null;
  const applicationId = positiveId(blocks.analysis.application_id);
  const runId = positiveId(blocks.analysis.run_id);
  if (applicationId == null || runId == null) return null;
  return {
    applicationId,
    runId,
    source: blocks.analysis.source === "plaid" ? ("plaid" as const) : ("statements" as const),
    stats: isRecord(blocks.stats) ? (blocks.stats as NonNullable<ChoiceBlocks["stats"]>) : null,
  };
}

/** The fields the card's tiles and rows don't show (the Application). */
function cardFigures(fields: ChoiceField[], stats: TransactionCard["stats"]): Figure[] {
  return fields
    .filter((field) => !field.key && !(stats?.group && field.group === stats.group))
    .map((field) => ({ label: field.label, value: field.value }));
}

/**
 * The card when the server gave a table row for every one of the choice's rows and the run it is
 * about; otherwise null, and the choice shows in the approval view like any other.
 */
export const transactionCard = perChoice(readTransactionCard);

function readTransactionCard(choice: WireChoice): TransactionCard | null {
  const blocks: unknown = choice.blocks;
  if (!isRecord(blocks) || !isRecord(blocks.transactions)) return null;
  const analysis = analysisOf(blocks);
  if (!analysis) return null;
  const table = new Map<string, BlockTransaction>();
  const raw = blocks.transactions.rows;
  for (const item of Array.isArray(raw) ? raw : []) {
    const transaction = blockTransaction(item);
    if (transaction) table.set(transaction.key, transaction);
  }
  const fields = cardRows(choice);
  const keyed = fields.filter((field) => field.key);
  if (!keyed.length || !keyed.every((field) => table.has(field.key!))) return null;
  return {
    ...analysis,
    rows: keyed.map((field) => ({
      key: field.key!,
      ticked: field.ticked === true,
      group: field.group ?? "",
      label: field.label,
      disabled: field.disabled ?? "",
      state: field.state ?? null,
      line: field.line ?? "",
      transaction: table.get(field.key!)!,
    })),
    figures: cardFigures(fields, analysis.stats),
  };
}

/** A list card row drawn as one field of a position: the row's tick and outcome, and its change. */
export interface PositionRow {
  key: string;
  ticked: boolean;
  /** Why the row can't be ticked (a reopened card's call no longer has it), or "". */
  disabled: string;
  state: ChoiceField["state"] | null;
  line: string;
  change: BlockPositionRow;
}

/** A position the card changes, with its field rows in the card's order. */
export interface PositionEntry {
  /** The rows' heading: "OnDeck · $500.00 weekly". */
  group: string;
  position: BlockPosition;
  rows: PositionRow[];
}

/** An approval whose rows are position fields (correct_position): the run, figures, positions. */
export interface PositionCard {
  applicationId: number;
  runId: number;
  source: "statements" | "plaid";
  stats: TransactionCard["stats"];
  positions: PositionEntry[];
  /** Every position's rows, in the card's order. */
  rows: PositionRow[];
  figures: Figure[];
}

const POSITION_FIELDS: readonly BlockPositionRow["field"][] = [
  "include_in_leverage",
  "frequency",
  "status",
  "funder_name",
  "payment_amount",
];
const POSITION_KINDS: readonly BlockPosition["kind"][] = ["mca", "loc", "loan"];

const blockCount = (value: unknown) =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : 0;

function positionSide(raw: unknown): BlockPositionSide {
  const side = isRecord(raw) ? raw : {};
  return {
    funder: blockText(side.funder),
    amount: blockText(side.amount),
    frequency: blockText(side.frequency),
    status: blockText(side.status),
    in_leverage: side.in_leverage === true,
    leverage_override:
      side.leverage_override === "in" || side.leverage_override === "out"
        ? side.leverage_override
        : "",
    scheduled: typeof side.scheduled === "boolean" ? side.scheduled : side.frequency !== "unknown",
    payments: blockCount(side.payments),
    funded: blockText(side.funded),
    funding_date: blockText(side.funding_date),
  };
}

function positionRow(raw: unknown): BlockPositionRow | null {
  if (!isRecord(raw) || !blockText(raw.key)) return null;
  const field = POSITION_FIELDS.find((name) => name === raw.field);
  // A payment can come as a number (the args' decimal).
  const to = typeof raw.to === "number" ? raw.to.toFixed(2) : raw.to;
  if (!field || (typeof to !== "string" && typeof to !== "boolean")) return null;
  return {
    key: blockText(raw.key),
    field,
    label: blockText(raw.label),
    old: blockText(raw.old),
    new: blockText(raw.new),
    to,
    extended: raw.extended === true,
  };
}

function blockPosition(raw: unknown): BlockPosition | null {
  if (!isRecord(raw) || !blockText(raw.key)) return null;
  const rows = (Array.isArray(raw.rows) ? raw.rows : []).map(positionRow);
  if (!rows.length || rows.some((row) => row === null)) return null;
  const counts = isRecord(raw.counts) ? raw.counts : {};
  return {
    key: blockText(raw.key),
    funder: blockText(raw.funder),
    kind: POSITION_KINDS.find((kind) => kind === raw.kind) ?? "mca",
    rows: rows as BlockPositionRow[],
    now: positionSide(raw.now),
    after: positionSide(raw.after),
    transactions: (Array.isArray(raw.transactions) ? raw.transactions : [])
      .map(blockTransaction)
      .filter((row): row is BlockTransaction => row !== null),
    counts: {
      transactions: blockCount(counts.transactions),
      payments: blockCount(counts.payments),
      fundings: blockCount(counts.fundings),
      returns: blockCount(counts.returns),
    },
  };
}

/**
 * The card when the server gave a position for every one of the choice's rows and the run it is
 * about; otherwise null, and the choice shows in the approval view like any other.
 */
export const positionCard = perChoice(readPositionCard);

function readPositionCard(choice: WireChoice): PositionCard | null {
  const blocks: unknown = choice.blocks;
  if (!isRecord(blocks) || !Array.isArray(blocks.positions)) return null;
  const analysis = analysisOf(blocks);
  if (!analysis) return null;
  const positions = blocks.positions.map(blockPosition);
  if (!positions.length || positions.some((position) => position === null)) return null;
  const owner = new Map<string, { position: BlockPosition; change: BlockPositionRow }>();
  for (const position of positions as BlockPosition[]) {
    for (const change of position.rows) owner.set(change.key, { position, change });
  }
  const fields = cardRows(choice);
  const keyed = fields.filter((field) => field.key);
  if (!keyed.length || !keyed.every((field) => owner.has(field.key!))) return null;
  const rows: PositionRow[] = keyed.map((field) => ({
    key: field.key!,
    ticked: field.ticked === true,
    disabled: field.disabled ?? "",
    state: field.state ?? null,
    line: field.line ?? "",
    change: owner.get(field.key!)!.change,
  }));
  const groups = new Map(keyed.map((field) => [field.key!, field.group ?? ""]));
  return {
    ...analysis,
    positions: (positions as BlockPosition[]).flatMap((position) => {
      const own = rows.filter((row) => owner.get(row.key)!.position === position);
      return own.length ? [{ group: groups.get(own[0].key)!, position, rows: own }] : [];
    }),
    rows,
    figures: cardFigures(fields, analysis.stats),
  };
}
